import { PLAN_OFFERS, TRIAL_PERIOD_DAYS, monthlyPrice, planComparison, type ComparisonCell, type PlanOffer } from '@flexbyt/shared';
import { cx } from '@flexbyt/widget-preview';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHero } from '../components/ui/hero.js';
import { PricingScene } from '../components/mascot/scenes.js';
import { CustomerMarquee } from '../components/home/CustomerMarquee.js';
import { Testimonials } from '../components/Testimonials.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { PRICING_FAQS } from '../content/pricing.js';
import { fetchTestimonials } from '../content/testimonials.js';
import { WIDGET_BUILDER_PATH } from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * Pricing.
 *
 * ===========================================================================
 * Why this page did not exist until the audit
 * ===========================================================================
 * `/pricing` is in `PRESERVED_PATHS`, is linked from the primary navigation,
 * the homepage and the agency page — and had no route. It would have 404'd on
 * launch from the header of every page on the site. The dead-link sweep is what
 * found it; no phase had been asked to build it, and every phase assumed
 * another one had.
 *
 * ===========================================================================
 * The shape: free above, the two paid plans below
 * ===========================================================================
 * Not three equal cards. Free is not a cheaper Professional — it is what you
 * get without making a decision — and a three-column row made the first act of
 * reading this page an act of comparison between three things, one of which is
 * not really in the comparison. The band and the row are documented where they
 * are built; the short version is that the page now asks "start here, it costs
 * nothing" before it asks "and if that is not enough, which of these two".
 *
 * The cycle toggle and the trial banner live with the paid plans for the
 * plainest reason available: neither says anything true about a plan that costs
 * nothing.
 *
 * ===========================================================================
 * Where the customer reviews sit, and why they are on this page at all
 * ===========================================================================
 * They came from the homepage, where they were answering a question nobody had
 * yet. Somebody on the homepage is still working out what ReviewsJet is;
 * testimony answers "is it worth paying for", and this is the page where that
 * becomes the actual question.
 *
 * The section sits after the feature table and before the FAQ, which makes the
 * page read in the order a reader's doubts arrive: here is what it costs, here
 * is exactly what each plan gets you, here is what people who paid say about
 * it, here is what you are probably still worried about.
 *
 * It sits on white. It was briefly `tone="tinted"`, on the argument that this
 * is the page's turning point — and a coloured ground under a grid of review
 * cards is the one place that argument costs more than it buys: it tints the
 * section whose whole job is to look like real reviews rather than like
 * marketing. The page separates its bands by alternating white and `ink-25`
 * instead, which is what the FAQ underneath is `raised` for.
 *
 * ===========================================================================
 * Every number here is computed
 * ===========================================================================
 * The cards read `PLAN_OFFERS` and the table reads `planComparison()`, which
 * builds each cell from the same entitlement helpers the API enforces with —
 * `hasAutoSync`, `hasCsvImport`, `isPaidPlan`. Nothing on this page is typed
 * out by hand, because a pricing page maintained by hand drifts from the
 * software, and it always drifts towards promising more.
 */
export const Route = createFileRoute('/pricing')({
  head: () => {
    /*
      The trial is in the title, not only in the description.

      A search result gives you about sixty characters of title and a
      description Google rewrites at will — so the one line you can count on a
      person reading is the title, and "14-day free trial" is the single most
      useful thing this page can put there. "Pricing" alone says nothing a
      reader cannot already infer from the URL.

      `TRIAL_PERIOD_DAYS` rather than "14", for the same reason the cards use
      it: the number in the search result is then the number Stripe actually
      grants, and neither can be changed without the other following.
    */
    const { meta, links } = seo({
      title: `Pricing — free plan, and a ${TRIAL_PERIOD_DAYS}-day free trial`,
      description:
        `Free to start, $7.99 or $19.99 a month after that, and every paid plan begins with a ${TRIAL_PERIOD_DAYS}-day free trial — no charge until it ends. Every widget layout on every plan, and no view limits.`,
      path: '/pricing',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Pricing', path: '/pricing' },
            ])
          ),
        },
        {
          type: 'application/ld+json',
          children: jsonLd(faqSchema(PRICING_FAQS)),
        },
      ],
    };
  },
  /*
    Loaded on the server, so ReviewsJet's own customer reviews are in the HTML a
    crawler receives. Social proof that only appears after hydration does not
    exist as far as search is concerned — and it never throws: `fetchTestimonials`
    answers with a marked placeholder set for every failure, so the API being
    slow is not a reason for the pricing page to be down.
  */
  loader: () => fetchTestimonials(),
  component: Pricing,
});

type Cycle = 'monthly' | 'annual';

function Pricing() {
  const [cycle, setCycle] = useState<Cycle>('monthly');
  const testimonials = Route.useLoaderData();

  /*
    Split by price rather than by id, so a fourth tier appearing in
    `PLAN_OFFERS` lands in the paid row without anybody editing this file — and
    a catalogue that somehow ships no free tier renders the paid row alone
    rather than crashing on an index.
  */
  const free = PLAN_OFFERS.find((offer) => offer.monthly === 0);
  const paid = PLAN_OFFERS.filter((offer) => offer.monthly > 0);

  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Pricing', path: '/pricing' },
        ]}
        art={<PricingScene trialDays={TRIAL_PERIOD_DAYS} />}
      >
        <Measure>
          <Eyebrow>Pricing</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            Priced by how many reviews you <span className="whitespace-nowrap"><Mark>show</Mark>,</span> not how many
            people see them.
          </Heading>
          <Text size="lead" className="mt-6">
            Every layout works on every plan, including free. There is no view
            limit on any plan, so a page that does well does not cost more.
          </Text>

          {/*
            =================================================================
            The trial, said once and said early
            =================================================================
            It used to be a wide tinted banner sitting above the cards, which
            was loud in the way a banner is loud — a coloured bar you skip
            because coloured bars are usually a cookie notice or a sale. And it
            was in the wrong place: a reader meets it *after* they have started
            comparing prices, when the decision has already been framed as
            "which do I buy".

            Here it is a line under the lead paragraph, in the flow of the
            sentence a person is already reading, and it reframes the whole page
            before they reach a single number: not "which do I buy" but "which
            do I try". The tick is the only ornament, `positive-700` rather than
            the accent because the accent belongs to the buttons underneath and
            a second blue mark this close would read as another action.

            Repeated exactly twice more on the page, both times where it is
            load-bearing rather than decorative: under each paid plan's button,
            where somebody is about to click it, and in the small print, where
            the conditions are.
          */}
          <p className="mt-6 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-body text-ink-700">
            <TrialTick />
            <span>
              <span className="font-semibold text-ink-950">
                Both paid plans start with a {TRIAL_PERIOD_DAYS}-day free trial.
              </span>{' '}
              Cancel inside it and you are not charged.
            </span>
          </p>
        </Measure>
      </PageHero>

      <Section size="tight" className="pt-10">
        {/*
          =====================================================================
          Free on its own row, the two paid plans underneath
          =====================================================================
          This was three equal cards in a `lg:grid-cols-3`, and the shape was
          arguing against the page. Three columns say "these are three
          comparable options, pick one", which is not what is on offer: Free is
          not a cheaper Professional, it is the thing you get without deciding
          anything, and putting it shoulder to shoulder with two priced cards
          made the first act of reading this page an act of comparison.

          Splitting them makes the page ask its questions in order. *Start here,
          it costs nothing* — one wide band, no price to weigh, no neighbour to
          weigh it against. Then, underneath and only for a reader who has
          decided that is not enough: *and if you outgrow it, these two.*

          It also lets each half be shaped for its own content. The free plan
          reads across — name and price on the left, what you get on the right —
          because a full-width column holding four short lines is mostly empty
          space. The paid cards read down, because two of them side by side are
          being compared line against line and columns are what make that
          possible.

          The cycle toggle and the trial banner moved down with the paid plans,
          for the plainest reason there is: neither says anything true about a
          plan that costs nothing. A monthly/annual switch above a $0 card is a
          control with no effect, and a fourteen-day trial banner over it is an
          offer that does not apply.
        */}
        {free ? <FreePlanBand offer={free} /> : null}

        {paid.length > 0 ? (
          <div className="mt-14">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <Heading level={2} size="title" id="paid-heading">
                  When the free plan runs out.
                </Heading>
                <Text size="small" tone="subtle" className="mt-2 max-w-prose">
                  More reviews, more sources, and the branding line gone. Both
                  start with a {TRIAL_PERIOD_DAYS}-day trial.
                </Text>
              </div>

              <CycleToggle cycle={cycle} onChange={setCycle} />
            </div>

            <PaidPlans offers={paid} cycle={cycle} />
          </div>
        ) : null}

        {/*
          The small print that keeps the trial line above honest. Every clause
          is a fact from the billing code rather than a hedge:

            once per workspace   `qualifiesForTrial` is `!billing.hadTrial`
            a card is taken      the checkout session sets no
                                 `payment_method_collection`, so Stripe collects
                                 one up front in subscription mode
            not on Wix           `hadTrial` is never set for Wix installs — the
                                 trial is a Stripe-checkout thing only
        */}
        <p className="mt-6 max-w-prose text-caption text-ink-500">
          Prices in USD. The {TRIAL_PERIOD_DAYS}-day trial runs once per
          workspace and starts when you subscribe — your card is taken at
          checkout and charged when the trial ends, so cancelling before then
          costs nothing. Wix installations are billed by Wix rather than by us,
          trial included, because an app distributed through their market has to
          charge through their market.
        </p>

        {/*
          The logo strip sits under the cards rather than above them, because
          the question it answers — "is anyone else paying for this" — is one a
          reader only has once they have seen the prices.

          It is not made redundant by the customer reviews further down, and the
          two are deliberately far apart. Logos answer *who*, in one glance,
          while somebody is still scanning; the reviews answer *was it worth it*,
          in sentences, once they have stopped. Stacking them would turn two
          different arguments into one long block of reassurance, which is the
          shape a reader discounts wholesale.
        */}
        <CustomerMarquee className="mt-16 md:mt-20" />
      </Section>

      <FeatureTable />

      {/* ---------------------------------------------- Customer reviews */}
      {/*
        White, not the tinted band it started on.

        `tone="tinted"` is `accent-50`, and a blue ground under a grid of white
        review cards did two things wrong at once: it tinted the one section
        whose whole job is to look like real reviews rather than like marketing,
        and it made the cards read as a component dropped onto a coloured panel
        instead of as a section of the page. Reviews want a neutral ground —
        that is why every widget on this site draws them on white.

        The band rhythm still works because the FAQ underneath took `raised`:
        the page now alternates white, ink-25, white, ink-25, ink rather than
        needing a colour to separate two neighbours.
      */}
      <Section aria-labelledby="customers-heading">
        <Testimonials testimonials={testimonials} />
      </Section>

      <Section tone="raised" aria-labelledby="faq-heading">
        <Measure>
          <Heading level={2} id="faq-heading">
            Questions about the plans.
          </Heading>
        </Measure>
        <dl className="mt-10 max-w-text">
          {PRICING_FAQS.map((faq, index) => (
            <div
              key={faq.q}
              className={index > 0 ? 'mt-8 border-t border-ink-150 pt-8' : ''}
            >
              <dt className="text-title text-ink-950">{faq.q}</dt>
              <dd className="mt-2.5 text-body text-ink-600">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section tone="ink" size="base">
        <Measure>
          <Heading level={2} className="text-white">
            Try it before you decide.
          </Heading>
          <Text className="mt-4 text-white/70">
            Build a widget with sample reviews — no account, no card. What you
            build is waiting for you if you sign up.
          </Text>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="inverse" size="lg">
              Build a widget free
            </ButtonLink>
            <ButtonAnchor
              href={startUrl('pricing-close')}
              variant="inverse-ghost"
              size="lg"
              onClick={() =>
                track('pricing_cta_clicked', { surface: 'pricing-close' })
              }
            >
              Start free
            </ButtonAnchor>
          </div>
        </Measure>
      </Section>
    </>
  );
}

/**
 * Monthly / annual.
 *
 * A pair of real radio inputs rather than a styled div: this changes every
 * price on the page, so it has to be reachable by keyboard and announced as a
 * choice with a current value. A `<div onClick>` here is the single most common
 * accessibility failure on a pricing page.
 */
function CycleToggle({
  cycle,
  onChange,
}: {
  cycle: Cycle;
  onChange: (next: Cycle) => void;
}) {
  return (
    <fieldset className="flex items-center gap-1 rounded-full bg-ink-50 p-1 ring-1 ring-ink-150 sm:w-fit">
      <legend className="sr-only">Billing period</legend>
      {(['monthly', 'annual'] as const).map((value) => (
        <label
          key={value}
          className={cx(
            'flex-1 cursor-pointer rounded-full px-4 py-1.5 text-center text-body-sm transition-colors sm:flex-none',
            'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent-600',
            cycle === value
              ? 'bg-white text-ink-950 shadow-xs'
              : 'text-ink-600 hover:text-ink-900'
          )}
        >
          <input
            type="radio"
            name="billing-cycle"
            value={value}
            checked={cycle === value}
            onChange={() => onChange(value)}
            className="sr-only"
          />
          {value === 'monthly' ? 'Monthly' : 'Annual'}
          {value === 'annual' && (
            <span className="ml-1.5 text-caption text-accent-700">
              save ~17%
            </span>
          )}
        </label>
      ))}
    </fieldset>
  );
}

/** The tick in the trial banner. `accent-600` to match the type beside it. */
function TrialTick() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="size-4 shrink-0 text-accent-600"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m3.5 8.5 3 3 6-7" />
    </svg>
  );
}

/**
 * The free plan, across the top.
 *
 * ===========================================================================
 * It reads across because it has nothing to be compared with
 * ===========================================================================
 * The two paid cards below are columns because two columns is what lets
 * somebody read "150 published reviews" against "1,000 published reviews" on
 * one line. This has no counterpart, so a column shape would be borrowing the
 * appearance of a comparison that is not happening — and a full-width column
 * holding four short lines is mostly empty paper.
 *
 * So the price sits on the left, where a reader's eye starts, and what you get
 * for it sits on the right in two short columns rather than one long one. The
 * whole band is about the height of a paragraph, which is the right amount of
 * page to spend on "this part is free".
 *
 * ===========================================================================
 * Quieter than the cards, on purpose
 * ===========================================================================
 * `ink-25` with a hairline, no shadow, no border weight, and a secondary
 * button. Not because it matters less — it is the plan most readers of this
 * page will actually take — but because it is not asking for a decision. The
 * two cards underneath are; drawing this one with the same emphasis would put
 * three competing offers on a page whose first job is to say "start, it is
 * free" and get out of the way.
 */
function FreePlanBand({ offer }: { offer: PlanOffer }) {
  return (
    <div className="rounded-xl bg-ink-25 p-6 ring-1 ring-ink-150 sm:p-8">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-center lg:gap-14">
        <div>
          <div className="flex items-baseline gap-3">
            <h2 className="text-title text-ink-950">{offer.name}</h2>
            <span className="rounded-full bg-positive-50 px-2.5 py-0.5 text-caption font-medium text-positive-800 ring-1 ring-positive-200">
              No card needed
            </span>
          </div>

          <p className="mt-4 flex items-baseline gap-1.5">
            <span className="nums text-display text-ink-950">$0</span>
            <span className="text-body-sm text-ink-500">/month, forever</span>
          </p>
          <p className="mt-2 text-body-sm text-ink-600">{offer.audience}</p>

          <div className="mt-6 max-w-xs">
            <ButtonAnchor
              href={startUrl(`pricing-${offer.id}`)}
              size="md"
              block
              onClick={() =>
                track('pricing_cta_clicked', { surface: `pricing-${offer.id}` })
              }
            >
              Start free
            </ButtonAnchor>
          </div>
        </div>

        <div>
          <p className="eyebrow text-ink-500">What you get</p>
          {/*
            Two columns from `sm` up. Four short lines in one column beside a
            price block leaves the right half of a wide band empty, which is the
            shape that made this plan look like an afterthought when it was one
            third of a three-card row.
          */}
          <ul className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {offer.highlights.map((line) => (
              <li key={line} className="flex gap-2.5 text-body-sm text-ink-700">
                <Check />
                {line}
              </li>
            ))}
          </ul>

          {/*
            The one thing the highlight list cannot say, because it is not a
            limit. Every layout on every plan is the page's headline claim and
            the free plan is where a reader doubts it, so it is stated here
            rather than left to the comparison table four screens down.
          */}
          <p className="mt-5 border-t border-ink-150 pt-5 text-caption text-ink-500">
            Every widget layout, on every plan, with no limit on how many people
            see them. The free plan shows a small &ldquo;Powered by
            ReviewsJet&rdquo; line under your reviews.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The two paid plans, as one object rather than two.
 *
 * ===========================================================================
 * Why this is a panel and not two cards
 * ===========================================================================
 * Two bordered cards side by side is the shape every pricing page has, and it
 * has a specific failure that this page was showing: the cards are independent
 * objects, so their content lengths are independent too. Advanced unlocks four
 * things Professional does not, which meant one card ran to eleven lines and
 * the other stopped at eight — and because a grid stretches its children, the
 * short one ended in about a hundred pixels of nothing. A card with a hole in
 * the bottom of it reads as a card that ran out, not as a cheaper plan.
 *
 * One container split by a hairline fixes that by removing the premise. There
 * is no second card to be shorter than; there are two columns of one thing, and
 * a column that ends earlier than its neighbour is just a column. It is also
 * the truer picture: these are two tiers of one product, not two products.
 *
 * ===========================================================================
 * The recommendation is the ground, not a badge
 * ===========================================================================
 * Advanced used to be marked with a 2px ring *and* a "Most chosen" pill — two
 * devices doing one job, and the ring was competing with the hairline that
 * separates the halves. Its column is now drawn on ink. That is one decision
 * instead of two, it is unmissable at any distance, and it gives this row the
 * only real contrast on a page that is otherwise white paper and grey rules.
 *
 * The pill stays, because "Most chosen" is a *fact* and the dark ground is only
 * an emphasis — a reader should be told which plan most people pick, not left
 * to infer it from a colour.
 */
function PaidPlans({ offers, cycle }: { offers: PlanOffer[]; cycle: Cycle }) {
  return (
    <div className="mt-8 overflow-hidden rounded-2xl ring-1 ring-ink-200">
      {/*
        Two columns from `md` rather than `lg`. Three cards needed the wider
        breakpoint to avoid a squeezed middle column; two do not, so a tablet
        gets the side-by-side comparison that is the entire point of this row
        instead of a stack.

        `divide-y md:divide-x md:divide-y-0` is the spine: stacked on a phone
        the halves are separated by a horizontal rule, side by side by a
        vertical one. It is the same hairline either way, which is what keeps
        the panel reading as one object at every width.
      */}
      <div className="grid divide-y divide-ink-150 md:grid-cols-2 md:divide-x md:divide-y-0">
        {offers.map((offer, index) => (
          <PlanColumn
            key={offer.id}
            offer={offer}
            cycle={cycle}
            /*
              What the higher tier inherits, named rather than re-listed. See
              `PlanColumn` — this is what turns Advanced's eleven lines into
              four and makes the two columns nearly the same height.
            */
            inherits={offer.recommended ? offers[index - 1]?.name : undefined}
            inheritedExtras={
              offer.recommended ? (offers[index - 1]?.extras ?? []) : []
            }
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One tier, as a column of the panel.
 *
 * ===========================================================================
 * "Everything in Professional, plus…" is computed, not typed
 * ===========================================================================
 * `PLAN_OFFERS` gives Advanced an `extras` list that opens with the things it
 * unlocks and then repeats everything Professional already had — which is the
 * right shape for the data and the wrong shape for a column, because seven
 * lines of which four are duplicates is a list a reader stops reading.
 *
 * So the duplicates are subtracted here and replaced by one line naming the
 * plan they came from. The subtraction is a `filter`, not a hand-written
 * "Everything in Professional plus these three" — a hand-written version is a
 * sentence that keeps claiming to be true after somebody adds a fifth feature
 * to Professional and forgets this file exists.
 */
function PlanColumn({
  offer,
  cycle,
  inherits,
  inheritedExtras,
}: {
  offer: PlanOffer;
  cycle: Cycle;
  /** The tier below this one, when there is one to inherit from. */
  inherits?: string;
  /** Its extras, so they can be subtracted from this column's list. */
  inheritedExtras: string[];
}) {
  const price = monthlyPrice(offer, cycle);
  const dark = offer.recommended;

  const inheritedSet = new Set(inheritedExtras);
  const ownExtras = (offer.extras ?? []).filter(
    (line) => !inheritedSet.has(line)
  );

  return (
    <div
      className={cx(
        'flex flex-col p-6 sm:p-8',
        dark ? 'bg-ink-950 text-white' : 'bg-white text-ink-900'
      )}
    >
      {/*
        A floor on the header row's height, so the "Most chosen" pill cannot
        make one column a pixel taller than the other. The two halves share a
        panel and are read across a hairline, which is exactly the arrangement
        where a one-pixel offset between two prices is visible — and it is the
        sort of thing that reads as sloppiness rather than as a bug.
      */}
      <div className="flex min-h-7 items-baseline justify-between gap-3">
        <h3
          className={cx('text-title', dark ? 'text-white' : 'text-ink-950')}
        >
          {offer.name}
        </h3>
        {dark && (
          /*
            `accent-400` on ink, not `accent-600`. The mid step is the one the
            token block designates for a mark that has to carry meaning on a
            dark ground unaided — 600 on ink-950 is close to unreadable, and a
            white pill here would be a third white object in a column that
            already has a heading and a button.
          */
          <span className="shrink-0 rounded-full bg-accent-400/15 px-2.5 py-0.5 text-caption font-medium text-accent-200 ring-1 ring-accent-400/30">
            Most chosen
          </span>
        )}
      </div>
      <p className={cx('mt-1 text-caption', dark ? 'text-white/55' : 'text-ink-500')}>
        {offer.audience}
      </p>

      <p className="mt-6 flex items-baseline gap-1.5">
        <span
          className={cx(
            'nums text-display-sm',
            dark ? 'text-white' : 'text-ink-950'
          )}
        >
          ${price.toFixed(2)}
        </span>
        <span className={cx('text-body-sm', dark ? 'text-white/55' : 'text-ink-500')}>
          /month
        </span>
      </p>
      {/*
        A fixed slot, so the annual line appearing does not shove one column's
        button down while the other's stays put. The two halves share a panel
        now, and a row where the buttons are on different lines is the one thing
        that would make it stop reading as one.
      */}
      <p
        className={cx(
          'mt-1 min-h-5 text-caption',
          dark ? 'text-white/55' : 'text-ink-500'
        )}
      >
        {cycle === 'annual' ? `Billed $${offer.annual.toFixed(2)} a year` : ''}
      </p>

      <div className="mt-6">
        <ButtonAnchor
          href={startUrl(`pricing-${offer.id}`)}
          variant={dark ? 'inverse' : 'secondary'}
          size="md"
          block
          onClick={() =>
            track('pricing_cta_clicked', { surface: `pricing-${offer.id}` })
          }
        >
          Start {TRIAL_PERIOD_DAYS}-day trial
        </ButtonAnchor>

        {/*
          `TRIAL_PERIOD_DAYS` is imported rather than typed as "14": the shared
          constant is the same number the checkout call hands Stripe, and its own
          comment exists so that the promise on screen and the days Stripe
          actually grants cannot drift apart.
        */}
        <p
          className={cx(
            'mt-2.5 text-center text-caption',
            dark ? 'text-white/55' : 'text-ink-500'
          )}
        >
          Free for {TRIAL_PERIOD_DAYS} days, then ${price.toFixed(2)}/month.
        </p>
      </div>

      <ul
        className={cx(
          'mt-7 space-y-2.5 border-t pt-6',
          dark ? 'border-white/15' : 'border-ink-100'
        )}
      >
        {offer.highlights.map((line) => (
          <li
            key={line}
            className={cx(
              'flex gap-2.5 text-body-sm',
              dark ? 'text-white/85' : 'text-ink-700'
            )}
          >
            <Check dark={dark} />
            {line}
          </li>
        ))}
      </ul>

      {inherits ? (
        <p
          className={cx(
            'mt-5 text-body-sm font-medium',
            dark ? 'text-white' : 'text-ink-950'
          )}
        >
          Everything in {inherits}, plus:
        </p>
      ) : null}

      {ownExtras.length > 0 && (
        <ul className={cx('space-y-2.5', inherits ? 'mt-3' : 'mt-4')}>
          {ownExtras.map((line) => (
            <li
              key={line}
              className={cx(
                'flex gap-2.5 text-body-sm',
                dark ? 'text-white/70' : 'text-ink-600'
              )}
            >
              <Check muted dark={dark} />
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The tick beside a feature.
 *
 * `dark` rather than letting it inherit `currentColor`: the accent at its 600
 * step is a blue that all but disappears on `ink-950`, and the muted grey
 * vanishes outright. On ink the same two roles are played by `accent-400` and a
 * translucent white — the mid step is what the token block designates for a
 * mark carrying meaning unaided on a dark ground.
 */
function Check({
  muted = false,
  dark = false,
}: {
  muted?: boolean;
  dark?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden
      className={cx(
        'mt-1 size-3.5 shrink-0',
        dark
          ? muted
            ? 'text-white/40'
            : 'text-accent-400'
          : muted
            ? 'text-ink-400'
            : 'text-accent-600'
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 10.5l4 4 8-9" />
    </svg>
  );
}

/**
 * The full matrix, built from the product's own entitlement helpers.
 */
function FeatureTable() {
  const planIds = PLAN_OFFERS.map((offer) => offer.id);
  const groups = planComparison(planIds);

  return (
    <Section tone="raised" aria-labelledby="table-heading">
      <Measure>
        <Heading level={2} id="table-heading">
          What each plan includes.
        </Heading>
        <Text className="mt-4 max-w-prose">
          Every cell below is read from the same rules the software enforces, so
          this table cannot promise something the product will refuse.
        </Text>
      </Measure>

      <div className="mt-10 overflow-x-auto">
        <table className="w-full min-w-[40rem] border-collapse text-left">
          <caption className="sr-only">
            ReviewsJet plans compared, feature by feature
          </caption>
          <thead>
            <tr className="border-b border-ink-200">
              <th scope="col" className="py-3 pr-4 text-caption font-semibold text-ink-950">
                Feature
              </th>
              {PLAN_OFFERS.map((offer) => (
                <th
                  key={offer.id}
                  scope="col"
                  className="py-3 pr-4 text-caption font-semibold text-ink-950"
                >
                  {offer.name}
                </th>
              ))}
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.title}>
              <tr>
                <th
                  scope="colgroup"
                  colSpan={PLAN_OFFERS.length + 1}
                  className="pt-8 pb-2 text-caption font-semibold tracking-wide text-ink-500 uppercase"
                >
                  {group.title}
                </th>
              </tr>
              {group.rows.map((row) => (
                <tr key={row.label} className="border-b border-ink-100">
                  <th scope="row" className="py-3.5 pr-4 align-top">
                    <span className="block text-body-sm font-medium text-ink-950">
                      {row.label}
                    </span>
                    {row.hint && (
                      <span className="mt-0.5 block text-caption text-ink-500">
                        {row.hint}
                      </span>
                    )}
                  </th>
                  {row.cells.map((cell, index) => (
                    <td
                      key={PLAN_OFFERS[index]!.id}
                      className="py-3.5 pr-4 align-top text-body-sm text-ink-700"
                    >
                      <Cell cell={cell} plan={PLAN_OFFERS[index]!.name} row={row.label} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>

      <p className="mt-6 max-w-prose text-caption text-ink-500">
        Building for clients?{' '}
        <SiteLink
          to="/agencies"
          className="font-medium text-accent-600 underline decoration-accent-200 underline-offset-[3px] hover:text-accent-700"
        >
          How agencies use ReviewsJet
        </SiteLink>{' '}
        — including what it does not do.
      </p>
    </Section>
  );
}

/**
 * One cell.
 *
 * The yes/no glyphs carry a visually hidden word each. A tick with no text is
 * unreadable to a screen reader, and "Automatic daily sync, Free, no" is the
 * whole content of the row for somebody navigating this table by cell.
 */
function Cell({
  cell,
  plan,
  row,
}: {
  cell: ComparisonCell;
  plan: string;
  row: string;
}) {
  if (cell.kind === 'text') return <>{cell.text}</>;

  const included = cell.kind === 'yes';
  return (
    <>
      <span className="sr-only">
        {row}, {plan}: {included ? 'included' : 'not included'}
      </span>
      {included ? (
        <Check />
      ) : (
        <span aria-hidden className="text-ink-300">
          —
        </span>
      )}
    </>
  );
}
